import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Download, FileText, ChevronDown, ChevronUp, Trash2 } from "lucide-react";
import { generateInvoicePDF } from "@/utils/invoicePdfExport";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface InvoiceItem {
  service_id: string;
  name: string;
  description: string | null;
  unit_price: number;
  quantity: number;
  total: number;
  type: string;
}

interface Invoice {
  id: string;
  invoice_number: string;
  invoice_date: string;
  amount: number;
  signature_url: string | null;
  invoice_items: InvoiceItem[] | null;
  patient: {
    name: string;
    email: string | null;
    phone: string | null;
    address: string | null;
    date_of_birth: string | null;
  };
  service: {
    name: string;
    price: number;
    description: string | null;
  } | null;
}

interface PractitionerInfo {
  business_name: string;
  practitioner_name: string;
  address: string;
  phone: string;
  email: string;
  siret: string | null;
}

interface InvoiceHistoryProps {
  practitionerInfo: PractitionerInfo | null;
}

export const InvoiceHistory: React.FC<InvoiceHistoryProps> = ({ practitionerInfo }) => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [filteredInvoices, setFilteredInvoices] = useState<Invoice[]>([]);
  const [selectedYear, setSelectedYear] = useState<string>("all");
  const [selectedMonth, setSelectedMonth] = useState<string>("all");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [expandedInvoices, setExpandedInvoices] = useState<Set<string>>(new Set());
  const [invoiceToDelete, setInvoiceToDelete] = useState<Invoice | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadInvoices();
  }, []);

  useEffect(() => {
    filterInvoices();
  }, [invoices, selectedYear, selectedMonth, dateFrom, dateTo]);

  const loadInvoices = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const { data, error } = await supabase
        .from("invoices")
        .select(`
          id,
          invoice_number,
          invoice_date,
          amount,
          signature_url,
          invoice_items,
          patient:patients(name, email, phone, address, date_of_birth),
          service:services(name, price, description)
        `)
        .eq("user_id", user.id)
        .order("invoice_date", { ascending: false });

      if (error) throw error;

      // Transform the data to flatten nested objects
      const transformedData = data?.map(invoice => ({
        ...invoice,
        patient: Array.isArray(invoice.patient) ? invoice.patient[0] : invoice.patient,
        service: Array.isArray(invoice.service) ? invoice.service[0] : invoice.service,
        invoice_items: Array.isArray(invoice.invoice_items) ? invoice.invoice_items as unknown as InvoiceItem[] : null,
      })) || [];

      setInvoices(transformedData as Invoice[]);
    } catch (error: any) {
      console.error("Erreur chargement factures:", error);
      toast.error("Erreur lors du chargement des factures");
    } finally {
      setLoading(false);
    }
  };

  const filterInvoices = () => {
    let filtered = [...invoices];

    // Filter by year
    if (selectedYear !== "all") {
      filtered = filtered.filter(inv => 
        new Date(inv.invoice_date).getFullYear().toString() === selectedYear
      );
    }

    // Filter by month
    if (selectedMonth !== "all") {
      filtered = filtered.filter(inv => 
        (new Date(inv.invoice_date).getMonth() + 1).toString() === selectedMonth
      );
    }

    // Filter by date range
    if (dateFrom) {
      filtered = filtered.filter(inv => inv.invoice_date >= dateFrom);
    }
    if (dateTo) {
      filtered = filtered.filter(inv => inv.invoice_date <= dateTo);
    }

    setFilteredInvoices(filtered);
  };

  const toggleExpanded = (invoiceId: string) => {
    const newExpanded = new Set(expandedInvoices);
    if (newExpanded.has(invoiceId)) {
      newExpanded.delete(invoiceId);
    } else {
      newExpanded.add(invoiceId);
    }
    setExpandedInvoices(newExpanded);
  };

  const reGenerateInvoicePDF = async (invoice: Invoice) => {
    if (!practitionerInfo) {
      toast.error("Informations du praticien manquantes");
      return;
    }

    try {
      // Convert invoice_items to the format expected by generateInvoicePDF
      const items = invoice.invoice_items?.map(item => ({
        serviceId: item.service_id,
        name: item.name,
        description: item.description,
        unitPrice: item.unit_price,
        quantity: item.quantity,
        total: item.total,
        type: item.type,
      }));

      const invoiceData = {
        ...invoice,
        items: items,
        service: invoice.service || undefined,
        practitioner: practitionerInfo,
      };

      generateInvoicePDF(invoiceData);
      toast.success("Facture régénérée avec succès");
    } catch (error: any) {
      console.error("Erreur régénération facture:", error);
      toast.error("Erreur lors de la régénération de la facture");
    }
  };

  const deleteInvoice = async () => {
    if (!invoiceToDelete) return;
    setDeleting(true);
    try {
      const { error } = await supabase
        .from("invoices")
        .delete()
        .eq("id", invoiceToDelete.id);
      if (error) throw error;
      setInvoices(prev => prev.filter(i => i.id !== invoiceToDelete.id));
      toast.success("Facture supprimée");
      setInvoiceToDelete(null);
    } catch (error: any) {
      console.error("Erreur suppression facture:", error);
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeleting(false);
    }
  };

  const getAvailableYears = () => {
    const years = new Set(invoices.map(inv => 
      new Date(inv.invoice_date).getFullYear().toString()
    ));
    return Array.from(years).sort((a, b) => b.localeCompare(a));
  };

  const getItemsCount = (invoice: Invoice) => {
    if (invoice.invoice_items && invoice.invoice_items.length > 0) {
      return invoice.invoice_items.length;
    }
    return invoice.service ? 1 : 0;
  };

  const getServiceDisplay = (invoice: Invoice) => {
    if (invoice.invoice_items && invoice.invoice_items.length > 0) {
      if (invoice.invoice_items.length === 1) {
        return invoice.invoice_items[0].name;
      }
      return `${invoice.invoice_items.length} articles`;
    }
    return invoice.service?.name || "N/A";
  };

  const months = [
    { value: "1", label: "Janvier" },
    { value: "2", label: "Février" },
    { value: "3", label: "Mars" },
    { value: "4", label: "Avril" },
    { value: "5", label: "Mai" },
    { value: "6", label: "Juin" },
    { value: "7", label: "Juillet" },
    { value: "8", label: "Août" },
    { value: "9", label: "Septembre" },
    { value: "10", label: "Octobre" },
    { value: "11", label: "Novembre" },
    { value: "12", label: "Décembre" },
  ];

  return (
    <Card className="shadow-card bg-gradient-card border-0">
      <CardHeader className="border-b border-border">
        <CardTitle className="text-2xl font-playfair text-primary flex items-center gap-2">
          <FileText className="w-6 h-6" />
          Historique des factures
        </CardTitle>
        <CardDescription>
          Consultez et régénérez vos factures passées
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6 pt-6">
        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label htmlFor="year">Année</Label>
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger id="year">
                <SelectValue placeholder="Toutes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les années</SelectItem>
                {getAvailableYears().map(year => (
                  <SelectItem key={year} value={year}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="month">Mois</Label>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger id="month">
                <SelectValue placeholder="Tous" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les mois</SelectItem>
                {months.map(month => (
                  <SelectItem key={month.value} value={month.value}>
                    {month.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="dateFrom">Date de début</Label>
            <Input
              id="dateFrom"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="dateTo">Date de fin</Label>
            <Input
              id="dateTo"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
        </div>

        {/* Invoices Table */}
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">
            Chargement des factures...
          </div>
        ) : filteredInvoices.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            Aucune facture trouvée
          </div>
        ) : (
          <>
            {/* Mobile: Cartes empilées */}
            <div className="block sm:hidden space-y-3">
              {filteredInvoices.map((invoice) => (
                <div key={invoice.id} className="p-4 rounded-lg border bg-card space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-mono font-bold text-primary text-sm">{invoice.invoice_number}</span>
                    <span className="font-semibold text-lg">{invoice.amount.toFixed(2)}€</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-muted-foreground text-xs">Date</p>
                      <p>{new Date(invoice.invoice_date).toLocaleDateString('fr-FR')}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Patient</p>
                      <p className="font-medium truncate">{invoice.patient?.name || "N/A"}</p>
                    </div>
                  </div>
                  <div className="text-sm">
                    <p className="text-muted-foreground text-xs">Service(s)</p>
                    <p className="text-primary truncate">{getServiceDisplay(invoice)}</p>
                  </div>
                  <div className="flex justify-end gap-2 pt-2 border-t">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => reGenerateInvoicePDF(invoice)}
                      className="gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Télécharger
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => setInvoiceToDelete(invoice)}
                      className="gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop: Tableau */}
            <div className="hidden sm:block rounded-lg border border-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead>N° Facture</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Patient</TableHead>
                    <TableHead>Service(s)</TableHead>
                    <TableHead className="text-right">Montant</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                {filteredInvoices.map((invoice) => (
                  <React.Fragment key={invoice.id}>
                    <TableRow className="cursor-pointer hover:bg-muted/50">
                      <TableCell onClick={() => toggleExpanded(invoice.id)}>
                        {getItemsCount(invoice) > 1 && (
                          expandedInvoices.has(invoice.id) 
                            ? <ChevronUp className="w-4 h-4 text-muted-foreground" />
                            : <ChevronDown className="w-4 h-4 text-muted-foreground" />
                        )}
                      </TableCell>
                      <TableCell className="font-medium">
                        {invoice.invoice_number}
                      </TableCell>
                      <TableCell>
                        {new Date(invoice.invoice_date).toLocaleDateString('fr-FR')}
                      </TableCell>
                      <TableCell>{invoice.patient?.name || "N/A"}</TableCell>
                      <TableCell>
                        <span className={getItemsCount(invoice) > 1 ? "text-primary font-medium" : ""}>
                          {getServiceDisplay(invoice)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {invoice.amount.toFixed(2)}€
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => reGenerateInvoicePDF(invoice)}
                            className="gap-2"
                          >
                            <Download className="w-4 h-4" />
                            Télécharger
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => setInvoiceToDelete(invoice)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {/* Expanded items row */}
                    {expandedInvoices.has(invoice.id) && invoice.invoice_items && invoice.invoice_items.length > 1 && (
                      <TableRow className="bg-muted/30">
                        <TableCell colSpan={7} className="py-2">
                          <div className="pl-8 space-y-1">
                            {invoice.invoice_items.map((item, idx) => (
                              <div key={idx} className="flex justify-between text-sm">
                                <span>
                                  • {item.name} (x{item.quantity})
                                </span>
                                <span className="font-medium">{item.total.toFixed(2)}€</span>
                              </div>
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
            </div>
          </>
        )}

        <div className="text-sm text-muted-foreground text-right">
          Total: {filteredInvoices.length} facture(s) - {filteredInvoices.reduce((sum, inv) => sum + inv.amount, 0).toFixed(2)}€
        </div>
      </CardContent>

      <AlertDialog open={!!invoiceToDelete} onOpenChange={(open) => !open && setInvoiceToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette facture ?</AlertDialogTitle>
            <AlertDialogDescription>
              La facture {invoiceToDelete?.invoice_number} sera définitivement supprimée de l'historique et de l'analytique. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); deleteInvoice(); }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? "Suppression..." : "Supprimer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
